import { Button, TreeSelect } from 'antd';
import { INFLATED_SPECIALTY, SpecialtyData } from 'constants/specialty';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TreeNode } from 'antd/lib/tree-select';
import { MenuActionsContainer, SecondaryTextButton, StyledDivider } from '../styledComponents';
import generateKey from '../../../../utils/generateKey';
import { useKeycloakReady } from 'hooks/useKeycloakReady';
import { useGetSpecialtyData } from 'all-in-one-shared-api';

type SpecialtyTreeSelectProps = {
  handleOnSpecialtyChange: Dispatch<SetStateAction<string[]>>;
  specialtyValues: string[] | undefined;
  isChecked: boolean;
};

export const SpecialtyTreeSelect = ({
  handleOnSpecialtyChange,
  specialtyValues,
  isChecked,
}: SpecialtyTreeSelectProps) => {
  const { t, i18n } = useTranslation();
  const { token } = useKeycloakReady();
  const [openSpecialty, setOpenSpecialty] = useState(false);
  const [key, setKey] = useState('');
  const [listData, setListData] = useState<SpecialtyData[]>([]);
  const { isLoading, shouldFetch } = useGetSpecialtyData('useGetSpecialtyData', token || '', {
    onSuccess: (res) => {
      setListData(res);
      shouldFetch(false);
    },
    onError: () => {
      shouldFetch(false);
    },
  });

  useEffect(() => {
    shouldFetch(!!key && !!token);
  }, [shouldFetch, key, token]);

  useEffect(() => {
    setKey(generateKey);
  }, [i18n.language]);

  const inflatedSpecialties = specialtyValues?.map((s) => INFLATED_SPECIALTY(s, listData)) ?? [];

  const onSpecialtyChanged = (values: string[]) => {
    const trimmedValues = values.map((val) => val.split('|')[0]);
    if (trimmedValues.includes('ALL')) {
      if (listData && listData.length > 0) {
        handleOnSpecialtyChange(listData.map((a: SpecialtyData) => `${a.dbValue.toUpperCase()}`));
      }
    } else {
      handleOnSpecialtyChange(trimmedValues);
    }
  };

  const onDeselectSpecialty = (value: string) => {
    const trimmedValue = value.split('|')[0];
    if (trimmedValue === 'ALL') {
      handleOnSpecialtyChange([]);
    } else {
      handleOnSpecialtyChange((prevState) => prevState.filter((a) => a !== trimmedValue && a !== 'ALL'));
    }
  };

  return (
    <TreeSelect
      key={key}
      style={{ width: '100%' }}
      placeholder={t('panelList.selectSpecialty')}
      value={inflatedSpecialties}
      onChange={onSpecialtyChanged}
      onDeselect={onDeselectSpecialty}
      multiple
      maxTagCount={1}
      disabled={isChecked}
      open={openSpecialty}
      onBlur={() => setKey(generateKey())}
      onDropdownVisibleChange={(visible) => setOpenSpecialty(visible)}
      treeCheckable
      tagRender={(node) => {
        if (listData.length > 0 && !isLoading) {
          return (
            <span className="ant-select-selection-item">
              <span className="ant-select-selection-item-content">{node.label}</span>
            </span>
          );
        }
        return <></>;
      }}
      dropdownRender={(menu) => (
        <>
          {menu}
          <StyledDivider />
          <MenuActionsContainer>
            <SecondaryTextButton type="text" onClick={() => handleOnSpecialtyChange([])}>
              {t('common.reset')}
            </SecondaryTextButton>
            <Button type="primary" onClick={() => setOpenSpecialty(false)}>
              {t('common.ok')}
            </Button>
          </MenuActionsContainer>
        </>
      )}
      showArrow
    >
      {listData
        .sort((a, b) => compareSpecialtyNameFn(a, b))
        .map((specialty) => (
          <TreeNode
            key={`${specialty.dbValue.toUpperCase()}|${specialty.name_zh}`}
            value={`${specialty.dbValue.toUpperCase()}|${specialty.name_zh}`}
            label={i18n.language === 'en' ? specialty.name : specialty.name_zh}
          />
        ))}
    </TreeSelect>
  );
};

const compareSpecialtyNameFn = (a: SpecialtyData, b: SpecialtyData) => {
  // According to https://generali-hk.atlassian.net/browse/AIOBRB-1986, the Specialties should be sorted by the English name
  return a.name > b.name ? 1 : -1;
};
